/**
 * School directory API — the platform-curated registry of real schools and the
 * classes inside them.
 *
 * See docs/FeatureDesigns/school-teachers-parents/schoolclass-model-target.md. Two things
 * the shape of this client reflects:
 *
 *   - **Listing requires a filter.** The directory is unbounded (every school in
 *     the country, eventually), so `listSchools()` with nothing set returns
 *     nothing. Search is the entry point.
 *   - **Moving a school between the public directory and a tenant is its own
 *     call.** `tenantId` is never sent on save — where a record lives must not
 *     change as a side effect of renaming it.
 */
import type { ApiResponse } from '../types';
import { getErrorMessage } from '../types';
import { authApi } from './auth';
import { API_BASE, fetchWithAdmin } from './fetchHelper';
import type { ClassSharedApi, ExamOwner } from '@shared-ui/class-shared';

export interface School {
  id: string;
  /** Which of two things this record is, decided at creation: `'0'` = a public
   *  directory record; a tenant id = that customer's own school, whose classes,
   *  sections and students belong to them. Never the creator's, ambiently. */
  tenantId: string;
  name: string;
  city?: string;
  /** The boards this school OFFERS — plural, since plenty run CBSE and ICSE
   *  side by side. Which one a given CLASS follows is SchoolClass.board. */
  boards?: string[];
  /** @deprecated the single-board form; read only for un-migrated rows. */
  board?: string;
  createdAt: string;
  createdBy?: string;
  /** Resolved server-side — a user id in a column means nothing to a reader. */
  createdByName?: string;
  updatedBy?: string;
}

export interface SchoolClass {
  id: string;
  schoolId: string;
  /** Resolved server-side from `createdBy`. Absent when the account is gone —
   *  the id then stands in, since it is still an answer. */
  createdByName?: string;
  createdByEmail?: string;
  /** Always equal to its school's. */
  tenantId: string;
  academicYear: string;
  /** The GRADE. A class is the whole grade; "10-A" and "10-B" are its sections
   *  (`SchoolClassSection`), which is why there is no `section` here. */
  grade: string;
  /** Which of the school's boards THIS class follows — what a shared
   *  curriculum resolves from. */
  board?: string;
  /** Which subjects this class teaches. ABSENT means unknown (fall back to the
   *  caller's own); an empty array would mean a class that teaches nothing. */
  subjects?: string[];
  createdAt: string;
  createdBy?: string;
  updatedBy?: string;
}

/** One sitting. `scope` ABSENT means the whole curriculum — not the same as an
 *  empty array, which would mean a paper examining nothing. */
export interface ExamPaper {
  id: string;
  subjects: string[];
  label?: string;
  date: string;
  time?: string;
  durationMinutes?: number;
  scope?: string[];
  curriculumId?: string;
  maxMarks?: number;
}

export interface ClassExam {
  id: string;
  ownerId: string;
  name: string;
  type: 'unit-test' | 'class-test' | 'pre-board' | 'board' | 'mock';
  papers: ExamPaper[];
  createdAt: string;
  createdBy?: string;
}

export interface SchoolClassSection {
  id: string;
  schoolClassId: string;
  /** Always equal to its class's, which is its school's. */
  tenantId: string;
  /** 'B', 'Super-Whiz-1'. Unique within the class. */
  name: string;
  createdAt: string;
  createdBy?: string;
  /** Display name resolved server-side from `createdBy` — the list shows a
   *  person, not an id. See schools.ts SectionView. */
  createdByName?: string;
  updatedBy?: string;
}

export interface ClassMember {
  userId: string;
  name?: string;
  /** active | pending | declined | left. Sorted active-first by the API. */
  status: string;
  joinedAt: string;
  approvedBy?: string;
  /** Which section they sit in. ABSENT means not said — a member of the class
   *  who has not told anyone which room, which is a normal state. */
  sectionId?: string;
  sectionName?: string;
}

/** PUT/DELETE alongside the POST helper — the shared editors need all three. */
async function sendJson<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = authApi.getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (res.status === 401) {
    authApi.logout();
    throw new Error('Session expired. Please login again.');
  }
  const data: ApiResponse<T> = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(getErrorMessage(data.error) || `API error: ${res.status} ${res.statusText}`);
  }
  return data.data as T;
}
const putJson = <T>(path: string, body?: unknown) => sendJson<T>('PUT', path, body);
const deleteJson = <T>(path: string, body?: unknown) => sendJson<T>('DELETE', path, body);

export async function writeJson<T>(path: string, body?: unknown, method: 'POST' | 'PUT' = 'POST'): Promise<T> {
  const token = authApi.getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (res.status === 401) {
    authApi.logout();
    throw new Error('Session expired. Please login again.');
  }
  const data: ApiResponse<T> = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(getErrorMessage(data.error) || `API error: ${res.status} ${res.statusText}`);
  }
  return data.data as T;
}

/** One teacher named on a class or a section, ready to render. */
export interface AssignedTeacher { id: string; name: string }

/** What the class and each of its sections say about who teaches them. */
export interface ClassTeachersView {
  /** False on a public directory record; `reason` says why, for a disabled control. */
  assignable: boolean;
  reason?: string;
  assignments: Array<{
    scope: 'class' | 'section';
    id: string;
    name: string;
    subjectTeachers: Record<string, AssignedTeacher[]>;
  }>;
}

/** How wide a teacher reads. Absent on the server means `enrolled`. */
export type StudentReach =
  | { kind: 'enrolled' }
  | { kind: 'tenant' }
  | { kind: 'classes'; classIds: string[] };

export interface TenantTeacher {
  id: string;
  name: string;
  loginId?: string;
  subjects: string[];
  reach: StudentReach['kind'];
  classIds?: string[];
}

export const schoolsApi = {
  /** Search the directory. With no `search` or `tenantId` the backend returns
   *  nothing — deliberately, see the note above. */
  listSchools: (opts?: { search?: string; city?: string; tenantId?: string }) => {
    const p = new URLSearchParams();
    if (opts?.search) p.set('search', opts.search);
    if (opts?.city) p.set('city', opts.city);
    if (opts?.tenantId) p.set('tenantId', opts.tenantId);
    const qs = p.toString();
    return fetchWithAdmin<{ schools: School[]; total: number }>(`/system/schools${qs ? `?${qs}` : ''}`);
  },
  getSchool: (id: string) => fetchWithAdmin<School>(`/system/schools/${id}`),
  /** Create or rename. `tenantId` is ignored by the backend — use `setSchoolOwner`. */
  saveSchool: (s: Partial<School>) => writeJson<School>('/system/schools', s),
  /** Remove a directory record. Refused by the server unless the school is
   *  public and nothing real hangs off it — its empty classes and sections go
   *  with it. */
  deleteSchool: (id: string) => deleteJson(`/system/schools/${id}`),
  /** Move the record to a tenant, or pass '0' to move it to the public directory.
   *  Re-stamps the school's classes, and reports how many it touched. */
  setSchoolOwner: (id: string, tenantId: string) =>
    writeJson<{ school: School; classesRetagged: number }>(`/system/schools/${id}/owner`, { tenantId }),

  listClasses: (schoolId: string, academicYear?: string) => {
    const qs = academicYear ? `?academicYear=${encodeURIComponent(academicYear)}` : '';
    return fetchWithAdmin<{ schoolId: string; classes: SchoolClass[]; total: number }>(
      `/system/schools/${schoolId}/classes${qs}`,
    );
  },
  /** Repeating an identity tuple updates that class rather than adding a second. */
  saveClass: (schoolId: string, c: Partial<SchoolClass>) =>
    writeJson<SchoolClass>(`/system/schools/${schoolId}/classes`, c),

  /** Every section of a class, by name. */
  listSections: (schoolClassId: string) =>
    fetchWithAdmin<{ schoolClassId: string; sections: SchoolClassSection[]; total: number }>(
      `/system/school-classes/${schoolClassId}/sections`,
    ),
  /** Repeating a name updates that section rather than adding a second — "10 B"
   *  and "10-B" are one section, and two rows would split one roster in half. */
  /** Add a section, or — with `id` — rename that one. The id does NOT follow the
   *  name: every membership row names it. */
  saveSection: (schoolClassId: string, name: string, id?: string) =>
    writeJson<SchoolClassSection>(`/system/school-classes/${schoolClassId}/sections`, { name, id }),

  // ── Teaching staff ─────────────────────────────────────────────────────────
  // Under `console/tenants/{tenantId}` rather than `/system`, because a school's
  // own administrators set these — no platform involvement (TS-7a).

  /** Who teaches the class and each of its sections, with names resolved.
   *  `assignable: false` carries the reason a public record cannot be assigned. */
  getClassTeachers: (tenantId: string, classId: string) =>
    fetchWithAdmin<ClassTeachersView>(
      `/console/tenants/${tenantId}/school-classes/${classId}/teachers`,
    ),
  /** Replaces the whole map for the class. An empty array clears a subject. */
  setClassTeachers: (tenantId: string, classId: string, subjectTeachers: Record<string, string[]>) =>
    writeJson<{ assigned: number }>(
      `/console/tenants/${tenantId}/school-classes/${classId}/teachers`,
      { subjectTeachers }, 'PUT',
    ),
  setSectionTeachers: (
    tenantId: string, classId: string, sectionId: string, subjectTeachers: Record<string, string[]>,
  ) =>
    writeJson<{ assigned: number }>(
      `/console/tenants/${tenantId}/school-classes/${classId}/sections/${sectionId}/teachers`,
      { subjectTeachers }, 'PUT',
    ),

  /** The tenant's teaching staff and how wide each one reads. */
  listTenantTeachers: (tenantId: string) =>
    fetchWithAdmin<{ teachers: TenantTeacher[] }>(`/console/tenants/${tenantId}/teachers`),
  setTeacherReach: (tenantId: string, teacherId: string, reach: StudentReach) =>
    writeJson<{ reach: string }>(`/console/tenants/${tenantId}/teachers/${teacherId}/reach`, { reach }, 'PUT'),

  /** EVERY membership row, not just active — "declined" and "never asked" are
   *  different situations to be looking at. */
  listClassMembers: (schoolClassId: string) =>
    fetchWithAdmin<{ schoolClassId: string; members: ClassMember[]; total: number }>(
      `/system/school-classes/${schoolClassId}/members`,
    ),
  /** The escape hatch for a class the democratic join flow cannot fix from
   *  inside. Removal is soft — the row survives so a rejoin is the same one. */
  editClassMember: (
    schoolClassId: string, userId: string,
    action: 'add' | 'remove' | 'set-section',
    sectionId?: string | null,
  ) =>
    writeJson<{ changed: boolean }>(
      `/system/school-classes/${schoolClassId}/members`, { userId, action, sectionId },
    ),

  /** Read-only view kept for the admin listing. Editing goes through the SAME
   *  routes the students use — see `classSharedApi` below. */
  listClassExams: (schoolClassId: string) =>
    fetchWithAdmin<{ schoolClassId: string; exams: ClassExam[]; total: number }>(
      `/system/school-classes/${schoolClassId}/exams`,
    ),
};

/**
 * The class's shared records, through the SAME endpoints the student app uses.
 *
 * An admin passes the membership gate without being a member: these live in the
 * public tenant, owned by nobody in particular, so where no student or teacher
 * owns them the platform is the authority of last resort — and it is the repair
 * path the democratic rule otherwise lacks.
 *
 * Same routes, not parallel `/system` ones, so the two cannot drift apart in
 * behaviour and one shared editor can serve both apps.
 */
/** Exams hang off a level, and the route says which: the class's own, or one section's.
 *  The section's path is nested under its class so the server can check the section
 *  belongs to it without a lookup. */
export function examsPath(owner: ExamOwner): string {
  return owner.sectionId
    ? `/school-classes/${owner.schoolClassId}/sections/${owner.sectionId}/exams`
    : `/school-classes/${owner.schoolClassId}/exams`;
}

export const classSharedApi: ClassSharedApi = {
  listExams: (owner) => fetchWithAdmin(examsPath(owner)),
  createExam: (owner, body) => writeJson(examsPath(owner), body),
  updateExam: (owner, examId, body) => putJson(`${examsPath(owner)}/${examId}`, body),
  deleteExam: (owner, examId) => deleteJson(`${examsPath(owner)}/${examId}`),
  moveExam: (owner, examId, toSectionId) =>
    writeJson(`${examsPath(owner)}/${examId}/move`, { toSectionId }),
  getCoverage: (id, subject) =>
    fetchWithAdmin(`/school-classes/${id}/coverage?subject=${encodeURIComponent(subject)}`),
  reportCoverage: (id, body) => writeJson(`/school-classes/${id}/coverage`, body),
  listPlans: ({ ownerKind, ownerId }) => {
    const params = new URLSearchParams({ ownerKind });
    if (ownerId) params.set('ownerId', ownerId);
    return fetchWithAdmin(`/study-plans?${params}`);
  },
  createPlan: (body) => writeJson('/study-plans', body),
  editPlan: (planId, body) => putJson(`/study-plans/${planId}`, body),
  editPlanDetails: (planId, body) => putJson(`/study-plans/${planId}/details`, body),
  editPlanSubjects: (planId, body) => putJson(`/study-plans/${planId}/subjects`, body),
  deletePlan: (planId, ownerId) =>
    deleteJson(`/study-plans/${planId}?ownerId=${encodeURIComponent(ownerId)}`),
  setPlanVisibility: (planId, body) => writeJson(`/study-plans/${planId}/visibility`, body),
};
